import { memo, Fragment } from 'react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './WhyDialogue.module.scss';

// "テキスト**強調**テキスト" を <b> 付きの ReactNode に変換
const renderBold = (text: string): ReactNode =>
  text.split('**').map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));

interface Group {
  icon: string;
  label: string;
  tag?: string;
  items: string[];
}

export const WhyDialogue: FC = memo(function WhyDialogue() {
  const { t } = useTranslation();

  const group = (key: string, icon: string): Group => ({
    icon,
    label: t(`about:whyDialogue.${key}.label`),
    tag: t(`about:whyDialogue.${key}.tag`, { defaultValue: '' }),
    items: t(`about:whyDialogue.${key}.items`, { returnObjects: true }) as string[],
  });

  const phases = [
    { head: t('about:whyDialogue.before'), deco: 'fa-comment-dots', groups: [group('plan', 'fa-clipboard-list'), group('rethink', 'fa-redo')] },
    { head: t('about:whyDialogue.during'), deco: 'fa-users', groups: [group('cooperation', 'fa-users')] },
    { head: t('about:whyDialogue.after'), deco: 'fa-lightbulb', groups: [group('feedback', 'fa-comments')] },
  ];

  return (
    <section id="why-dialogue" className={`${classes.root} contentWidth`}>
      <div className={classes.header}>
        <span className={classes.q}>?</span>
        <div className={classes.headingWrap}>
          <span className={classes.badge}>{t('about:whyDialogue.badge')}</span>
          <h2 className={classes.title}>{t('about:whyDialogue.title')}</h2>
        </div>
      </div>

      <div className={classes.flow}>
        {phases.map((p, pi) => (
          <Fragment key={pi}>
            {pi > 0 && (
              <div className={classes.arrow}>
                <i className="fas fa-chevron-right" />
              </div>
            )}
            <div className={classes.phase}>
              <div className={classes.phaseHead}>
                {p.head}
                <i className={`fas ${p.deco} ${classes.phaseDeco}`} />
              </div>
              <div className={classes.phaseBody}>
                {p.groups.map((g, gi) => (
                  <div className={classes.group} key={gi}>
                    <div className={classes.groupHead}>
                      <span className={classes.groupIcon}><i className={`fas ${g.icon}`} /></span>
                      <span className={classes.groupLabel}>{g.label}</span>
                      {g.tag && <span className={classes.tag}>{g.tag}</span>}
                    </div>
                    <ul className={classes.list}>
                      {g.items.map((it, ii) => (
                        <li key={ii}>
                          <i className={`fas fa-check ${classes.check}`} />
                          <span>{renderBold(it)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </Fragment>
        ))}
      </div>

      <div className={classes.loop}>
        <span className={classes.loopArrow} />
        <span className={classes.loopLabel}>{t('about:whyDialogue.loop')}</span>
      </div>
    </section>
  );
});
